type Props = {
  page: number;
  totalPages: number;
  totalItems: number;
  pageSize: number;
  rangeStart: number;
  rangeEnd: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  pageSizeOptions?: readonly number[];
  className?: string;
};

function buildPageList(current: number, total: number): (number | "ellipsis")[] {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  const pages = new Set<number>([1, total, current, current - 1, current + 1]);
  const sorted = [...pages].filter((p) => p >= 1 && p <= total).sort((a, b) => a - b);
  const result: (number | "ellipsis")[] = [];
  for (let i = 0; i < sorted.length; i++) {
    if (i > 0 && sorted[i] - sorted[i - 1] > 1) result.push("ellipsis");
    result.push(sorted[i]);
  }
  return result;
}

const btnBase =
  "inline-flex items-center justify-center min-w-[2rem] h-8 px-2 text-sm rounded-md border transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed";

export default function Pagination({
  page,
  totalPages,
  totalItems,
  pageSize,
  rangeStart,
  rangeEnd,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 20, 50],
  className = "",
}: Props) {
  const pages = buildPageList(page, totalPages);

  return (
    <div
      className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-4 py-3 bg-[#f8fafc] border-t border-gray-200 ${className}`}
    >
      <p className="text-xs text-gray-500 order-2 sm:order-1">
        {totalItems === 0 ? (
          "표시할 항목이 없습니다"
        ) : (
          <>
            <span className="font-medium text-[#2c4964]">
              {rangeStart}–{rangeEnd}
            </span>
            <span className="text-gray-400 mx-1">/</span>
            <span className="font-medium text-[#2c4964]">{totalItems}</span>
            <span className="ml-1">건</span>
          </>
        )}
      </p>

      <div className="flex flex-wrap items-center gap-2 order-1 sm:order-2">
        <label className="flex items-center gap-1.5 text-xs text-gray-500">
          <span className="whitespace-nowrap">페이지당</span>
          <select
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            className="h-8 pl-2 pr-7 text-sm border border-gray-200 rounded-md bg-white text-[#2c4964] focus:outline-none focus:border-[#1977cc] focus:ring-1 focus:ring-[#1977cc]/20"
          >
            {pageSizeOptions.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>

        <div className="flex items-center gap-1">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => onPageChange(1)}
            className={`${btnBase} border-gray-200 text-gray-600 hover:bg-white`}
            aria-label="첫 페이지"
          >
            <i className="ri-skip-back-mini-line" />
          </button>
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => onPageChange(page - 1)}
            className={`${btnBase} border-gray-200 text-gray-600 hover:bg-white`}
            aria-label="이전 페이지"
          >
            <i className="ri-arrow-left-s-line" />
          </button>

          {pages.map((p, idx) =>
            p === "ellipsis" ? (
              <span key={`e-${idx}`} className="px-1 text-gray-400 text-sm">
                …
              </span>
            ) : (
              <button
                key={p}
                type="button"
                onClick={() => onPageChange(p)}
                className={`${btnBase} min-w-[2rem] ${
                  p === page
                    ? "border-[#1977cc] bg-[#1977cc] text-white"
                    : "border-gray-200 text-gray-600 hover:bg-white"
                }`}
                aria-current={p === page ? "page" : undefined}
              >
                {p}
              </button>
            )
          )}

          <button
            type="button"
            disabled={page >= totalPages}
            onClick={() => onPageChange(page + 1)}
            className={`${btnBase} border-gray-200 text-gray-600 hover:bg-white`}
            aria-label="다음 페이지"
          >
            <i className="ri-arrow-right-s-line" />
          </button>
          <button
            type="button"
            disabled={page >= totalPages}
            onClick={() => onPageChange(totalPages)}
            className={`${btnBase} border-gray-200 text-gray-600 hover:bg-white`}
            aria-label="마지막 페이지"
          >
            <i className="ri-skip-forward-mini-line" />
          </button>
        </div>
      </div>
    </div>
  );
}
